import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OnChangeValue } from 'react-select';

import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { PlayerPosition } from 'modules/teams/domain/enums/player-position';
import { PlayerApplication } from 'modules/teams/domain/interfaces/player-application';
import PositionSelect from 'modules/teams/ui/components/position-select';
import { PlayerNumberInput } from 'modules/teams/ui/components/styles';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { getBirthDay } from 'shared/utils';
import { UDCheckbox } from 'ud-form/components';
import { SelectOption } from 'ud-form/components/select/component';
import { UDEntityRow } from 'ud-ui/components';

/** Что игроку меняют прямо в строке. Оба поля — целевые значения, а не дельта. */
export interface PlayerApplicationDetails {
  number?: number;
  position?: PlayerPosition;
}

interface PlayerApplicationRowProps {
  application: PlayerApplication;
  /** Порядковый номер строки, уже готовый к показу. */
  index: number;
  isSelected: boolean;
  onToggle: (playerId: number) => void;
  /** Начало жеста выделения. Строка отдаёт само событие: чекбокс из жеста надо исключить. */
  onRowMouseDown?: (event: React.MouseEvent<HTMLElement>, playerId: number) => void;
  /** Строка под курсором во время жеста. */
  onRowMouseEnter?: (playerId: number) => void;
  /** Клик по строке — запасной путь к тому же переключению. */
  onRowClick?: (playerId: number) => void;
  /** Сохранить номер и амплуа. `false` в ответе — бэкенд правку не принял, поле откатывается. */
  onChangeDetails?: (application: PlayerApplication, details: PlayerApplicationDetails) => Promise<boolean>;
}

const STATUS_KEY: Record<ApplicationStatus, string> = {
  [ApplicationStatus.PENDING]: 'pending',
  [ApplicationStatus.ACCEPTED]: 'accepted',
  [ApplicationStatus.DECLINED]: 'declined',
};

/**
 * Амплуа в сторе живёт в двух регистрах: у игрока из состава команды оно уже строчное
 * (`team-player.data-mapper.ts:17`), а у заявки — как пришло из GraphQL, заглавными.
 * Селекту нужен доменный enum, то есть строчный вариант.
 */
const normalizePosition = (position?: string) => (position ? (position.toLowerCase() as PlayerPosition) : undefined);

/**
 * Строка состава команды в заявках v2: отметка, фото, ФИО, подзаголовок
 * «статус · дата рождения» и поля номера с амплуа.
 *
 * От карточки старого флоу (`championship-team-players/team-player-card.tsx`) отличается тем,
 * как поля сохраняются: не по двухсекундному дебаунсу, который слал запрос сам посреди набора
 * и терял последнее значение при быстром уходе со строки, а по `blur` (и по Enter) для номера
 * и по выбору для амплуа. Результат виден: на время запроса поля отключены, дальше тост,
 * а на ошибке номер возвращается к прежнему значению.
 *
 * Поля обёрнуты в блок, гасящий `mousedown` и `click`: иначе правка номера начинала бы жест
 * выделения строки (`use-drag-selection.ts`) — react-select рисует свой контрол дивами, и
 * фильтр по `input, select` из `UDEntityRow` его не ловит.
 */
const PlayerApplicationRow = ({
  application,
  index,
  isSelected,
  onToggle,
  onRowMouseDown,
  onRowMouseEnter,
  onRowClick,
  onChangeDetails,
}: PlayerApplicationRowProps) => {
  const { t } = useTranslation();
  const playerId = application.player.id;
  const position = normalizePosition(application.position);

  const [number, setNumber] = useState(application.number);
  const [isSaving, setIsSaving] = useState(false);

  // Стор — источник истины: после сохранения и после перечитывания заявок в поле должно
  // оказаться то, что лежит в сторе. Но подставляем только когда стор **изменился**:
  // безусловный `setNumber(application.number)` в эффекте затирает набранное, если эффект
  // монтирования успел отложиться до первого ввода (ровно это ловится в тестах).
  const syncedNumberRef = useRef(application.number);
  useEffect(() => {
    if (syncedNumberRef.current !== application.number) {
      syncedNumberRef.current = application.number;
      setNumber(application.number);
    }
  }, [application.number]);

  const handleToggle = useCallback(() => onToggle(playerId), [onToggle, playerId]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => onRowMouseDown?.(event, playerId),
    [onRowMouseDown, playerId],
  );
  const handleMouseEnter = useCallback(() => onRowMouseEnter?.(playerId), [onRowMouseEnter, playerId]);
  const handleClick = useCallback(() => onRowClick?.(playerId), [onRowClick, playerId]);

  const save = useCallback(
    async (details: PlayerApplicationDetails) => {
      if (!onChangeDetails) {
        return;
      }

      setIsSaving(true);
      const isSaved = await onChangeDetails(application, details);
      setIsSaving(false);

      if (!isSaved) {
        setNumber(application.number);
      }
    },
    [onChangeDetails, application],
  );

  const handleNumberChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseInt(event.target.value, 10);
    setNumber(Number.isNaN(parsed) ? undefined : Math.max(0, parsed));
  }, []);

  const handleNumberBlur = useCallback(() => {
    if (number === application.number) {
      return;
    }

    save({ number, position });
  }, [number, application.number, position, save]);

  // Enter не шлёт запрос сам, а снимает фокус: сохранение остаётся ровно в одном месте.
  const handleNumberKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
    }
  }, []);

  const handlePositionChange = useCallback(
    (option: OnChangeValue<SelectOption, false>) => {
      const nextPosition = option ? (option.value as PlayerPosition) : undefined;
      if (nextPosition === position) {
        return;
      }

      save({ number, position: nextPosition });
    },
    [position, number, save],
  );

  const stopRowGesture = useCallback((event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation(), []);

  const name = [application.player.lastName, application.player.firstName, application.player.middleName].filter(Boolean).join(' ');

  const subtitle = useMemo(() => {
    const parts = [t(`applications.v2.team.playerStatus.${STATUS_KEY[application.status]}`)];

    const birthDate = application.player.birthDate ? new Date(application.player.birthDate) : undefined;
    const birthDay = getBirthDay({ birthDate, fallback: '' });
    if (birthDay) {
      parts.push(birthDay);
    }

    return parts.join(' · ');
  }, [t, application.status, application.player.birthDate]);

  return (
    <UDEntityRow
      data-testid="player-application-row"
      index={index}
      avatar={application.player.logoUrl || USER_DEFAULT_AVATAR}
      title={name}
      subtitle={subtitle}
      isSelected={isSelected}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      // Обработчик отдаём только когда его есть кому обработать: по его наличию
      // `UDEntityRow` решает, рисовать ли строку кликабельной.
      onClick={onRowClick ? handleClick : undefined}
      actions={
        <>
          {onChangeDetails && (
            <div className="flex items-center gap-3" onMouseDown={stopRowGesture} onClick={stopRowGesture}>
              <PlayerNumberInput
                name="number"
                type="number"
                min={0}
                placeholder={t('applications.v2.team.numberPlaceholder')}
                aria-label={t('applications.v2.team.numberLabel', { name })}
                data-testid={`player-number-${playerId}`}
                value={number ?? ''}
                disabled={isSaving}
                onChange={handleNumberChange}
                onBlur={handleNumberBlur}
                onKeyDown={handleNumberKeyDown}
              />
              <PositionSelect
                name="position"
                inputId={`player-position-${playerId}`}
                aria-label={t('applications.v2.team.positionLabel', { name })}
                placeholder={t('applications.v2.team.positionPlaceholder')}
                value={position}
                isDisabled={isSaving}
                onChange={handlePositionChange}
              />
            </div>
          )}
          <UDCheckbox
            checked={isSelected}
            onChange={handleToggle}
            data-testid={`select-player-${playerId}`}
            aria-label={t('applications.v2.team.selectPlayer', { name })}
          />
        </>
      }
    />
  );
};

export default React.memo(PlayerApplicationRow);
